import { Epic } from 'redux-observable';
import { debounceTime, map, mergeMap } from 'rxjs/operators';
import { ofType } from '@alycecom/utils';
import { pickAll } from 'ramda';

import { applyBranding, fetchBranding } from '../branding/branding.actions';
import { applyMessageHeader } from '../../modules/RecipientModule/store/data/data.actions';
import {
  applyBrandingFields,
  applyBrandingImages,
  resetBrandingChanges,
  setupInitialBranding,
} from '../../modules/BrandingModule/store/branding.actions';
import { BrandingFields, IBranding } from '../../types/Branding.types';
import { BrandingImageName } from '../../modules/BrandingModule/types/Branding.types';

const pickColorsAndTextFields = pickAll([
  BrandingFields.backgroundColor,
  BrandingFields.tertiaryColor,
  BrandingFields.primaryColor,
  BrandingFields.secondaryColor,
  BrandingFields.headlineColor,
  BrandingFields.highlightColor,
  BrandingFields.primaryCtaColor,
  BrandingFields.secondaryCtaColor,
  BrandingFields.messageHeader,
  BrandingFields.termsUrl,
  BrandingFields.privacyPolicyLink,
  BrandingFields.contactSupportLink,
  BrandingFields.disclaimer,
  BrandingFields.companyName,
  BrandingFields.giftCardConfirmationMessaging,
]);

export const processBrandingColorAndTextFieldsEpic: Epic = action$ =>
  action$.pipe(
    ofType(applyBrandingFields),
    debounceTime(500),
    mergeMap(({ payload }) => [
      applyBranding(pickColorsAndTextFields(payload)),
      applyMessageHeader(payload.messageHeader),
    ]),
  );

const IMAGE_FIELDS_MAP = {
  [BrandingImageName.logo]: {
    id: 'logoId',
    url: 'logoUrl',
  },
  [BrandingImageName.backgroundImage]: {
    id: 'backgroundImageId',
    url: 'backgroundImageUrl',
  },
};

export const processBrandingImageEpic: Epic = action$ =>
  action$.pipe(
    ofType(applyBrandingImages),
    map(({ payload }) => {
      const imageIdProp = IMAGE_FIELDS_MAP[payload.name].id;
      const imageUrlProp = IMAGE_FIELDS_MAP[payload.name].url;
      return applyBranding({
        [imageIdProp]: payload.image.id,
        [imageUrlProp]: payload.image.url === '' ? null : payload.image.url,
      });
    }),
  );

export const setupInitialBrandingEpic: Epic = action$ =>
  action$.pipe(
    ofType(fetchBranding.fulfilled),
    map(({ payload }: { payload: IBranding }) => setupInitialBranding(payload)),
  );

export const resetBrandingEpic: Epic = action$ =>
  action$.pipe(
    ofType(resetBrandingChanges),
    mergeMap(({ payload }: { payload: IBranding }) => [
      applyBrandingFields(payload),
      applyBrandingImages({
        name: BrandingImageName.logo,
        image: { id: payload[BrandingFields.logoId], url: payload[BrandingFields.logoUrl] || '' },
      }),
      applyBrandingImages({
        name: BrandingImageName.backgroundImage,
        image: { id: payload[BrandingFields.backgroundImageId], url: payload[BrandingFields.backgroundImageUrl] || '' },
      }),
    ]),
  );

export const proxyBrandingEpics = [
  processBrandingColorAndTextFieldsEpic,
  processBrandingImageEpic,
  setupInitialBrandingEpic,
  resetBrandingEpic,
];
